import { lazy, ReactNode, Suspense, useEffect, useState } from "react";
import { TokenRefreshComponent } from "./auth/TokenRefreshComponent";
import "./appinitandstate/TocAvailabilityNotice.css";
import {
  Navigate,
  NavigateFunction,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import TocAvailabilityNotice from "./appinitandstate/TocAvailabilityNotice";
import Auth from "./auth/Auth";
import { SongsAvailability } from "./appinitandstate/SongsAvailability";
import { BackendServerAvailability } from "./appinitandstate/BackendServerAvailability";
import Profile from "./profile/Profile";
import ViewProfile from "./profile/ViewProfile";
import { update } from "./offline/UpdateWorker";
import {
  CurrentState,
  OfflineUpdateSummary,
} from "./offline/OfflineUpdateSummary";
import Home from "./Home";
import { AuthState } from "./appinitandstate/AuthState";
import Playlist from "./playlist/Playlist";
import { RoutePaths } from "./RoutePaths";
import {
  AppInitializationResult,
  initApp,
} from "./appinitandstate/AppInitializer";
import { LocalStorageDeletionNecessaryNotice } from "./appinitandstate/LocalStorageDeletionNecessaryNotice";
import { AuthFailedNotice } from "./appinitandstate/AuthFailedNotice";
import { StubLogin } from "./auth/stub/StubLogin";
import { JoinCrewDialog } from "./crew/JoinCrewDialog";
import { DownloadNecessityChecker } from "./offline/DownloadNecessityChecker";
import { AuthAlwaysAuthorizedStub } from "./auth/AuthAlwaysAuthorizedStub";
import { createClient, fetchExchange } from "@urql/core";
import { offlineExchange } from "@urql/exchange-graphcache";
import { Client, Exchange, Provider } from "urql";
import { AuthConfig, BackendConfig } from "./Config";
import { makeDefaultStorage } from "@urql/exchange-graphcache/default-storage";
import schema from "./gql/graphql.schema.json";
import {
  cacheHydratedPromise,
  onCacheHydrated,
} from "./appinitandstate/CacheHydratedPromise";
import {
  ParamValidatorPlaylistIdentifier,
  playlistIdentifierParamKey,
} from "./playlist/ParamValidatorPlaylistIdentifier";
import { Logout } from "./auth/Logout";
import { LoadingWithHeader } from "./common/LoadingWithHeader";
import PlaylistUrlImportDialog from "./playlist/import/url/PlaylistUrlImportDialog";
import { paramKey, ParamValidatorId } from "./playlist/ParamValidatorId";
import { bucketParamKey, Items } from "./items/Items";
import { InfoContact } from "./infocontact/InfoContact";
import { Help } from "./help/Help";
import { ManageCrews } from "./crew/ManageCrews";
import { ManageSongbooks } from "./songbook/ManageSongbooks";
import { ScanQrCode } from "./scanqr/ScanQrCode";
import "./App.css";
import { DemoMultiSelectableList } from "./common/components/multiselectablelist/DemoMultiSelectableList";
import { InvalidRouteElement } from "./InvalidRouteElement";
import { Item } from "./items/item/Item";
import {
  itemIdParamKey,
  ParamValidatorBucketAndItemId,
} from "./playlist/ParamValidatorBucketAndItemId";
import { SongUb } from "./gql/graphql";
import { LookupSongFunction } from "./common/LookupSongFunction";
import AuthImpl from "./auth/AuthImpl";
import { useTranslation } from "react-i18next";

const isDevModeOrTestStubsEnabled =
  import.meta.env.DEV || import.meta.env.VITE_ENABLE_TEST_STUBS === "true";
const TokenRefreshHarness = isDevModeOrTestStubsEnabled
  ? lazy(() => import("./auth/stub/TokenRefreshHarness"))
  : () => null;

//delete old GraphQL cache
indexedDB.deleteDatabase("songship-v2");

const storage = makeDefaultStorage({
  idbName: "songship-v3", // The name of the IndexedDB database
  maxAge: 366, // The maximum age of the persisted data in days
  onCacheHydrated,
});

const cache = offlineExchange({
  storage,
  schema,
  keys: {
    //manual casting (as string) to satisfy the type checker --> better idea? let me know.
    CrewInformationUB: (data) => data.identifier as string,
    OrganizationCrewUB: (data) => data.identifier as string,
    PlaylistItemUB: () => null,
    SongnumberUB: () => null,
    DerivedImageUB: () => null,
    PlaylistUB: (data) => data.identifier as string,
    SongnumberWithSongUB: () => null,
    SongUB: (data) => data.id as string,
  },
  updates: {
    Mutation: {
      deleteSongs: (_result, _, cache) => {
        // Invalidate the cache entries related to the deleted songs. This triggers a refetch of the relevant queries.
        cache.invalidate("SongUB");
      },
    },
  },
});

export const lgBreakpoint = "992px";
export const xlBreakpoint = "1200px";

const auth: Auth = (() => {
  if (isDevModeOrTestStubsEnabled && !AuthConfig.useProdSecurityInDevMode) {
    return new AuthAlwaysAuthorizedStub();
  }
  return new AuthImpl();
})();

const createGraphQlClient = (exchanges: Exchange[]) =>
  createClient({
    url: BackendConfig.baseUrl + "/graphql",
    preferGetMethod: false,
    fetchOptions: () => {
      // TODO: If this method is called, before the auth init process is finished, we might get undefined.

      // TODO https://github.com/Rebstokk/songship/issues/165:
      // TODO  We need to handle renewal of tokens as well.
      // TODO: However, if _this_ lambda (assigned to fetchOptions) is called every time, when we do a new fetch, also the access token is retaken from `auth`. Maybe no changes needed here.
      const token = auth.getAccessToken();
      return {
        headers: { authorization: token ? `Bearer ${token}` : "" },
      };
    },
    requestPolicy: "cache-and-network",
    exchanges: exchanges,
  });

//Workaround: We create two different clients, because, if we configure a cache, things are cached even with network-only policy. This leads to a huge amount (600mb plus) of Playlists in the cache.
const graphQlClient: Client = createGraphQlClient([cache, fetchExchange]);
//TODO: Find out what happens with cache size, if we always use the cache...
// const graphQlClientNetworkOnly: Client = createGraphQlClient([fetchExchange])

export function App() {
  const [songs, setSongs] = useState<readonly SongUb[] | undefined>(undefined);
  const [offlineUpdateSummary, setOfflineUpdateSummary] =
    useState<OfflineUpdateSummary>({ currentState: CurrentState.NOT_RUNNING });

  const [songsAvailability, setSongsAvailability] = useState(
    SongsAvailability.Undecided,
  );
  const [backendServerAvailability, setBackendServerAvailability] = useState(
    BackendServerAvailability.Undecided,
  );
  const [authState, setAuthState] = useState(AuthState.Undecided);
  const [downloadOfFilesNecessary, setDownloadOfFilesNecessary] =
    useState(false);

  const navigate: NavigateFunction = useNavigate();

  const location = useLocation();
  const { t } = useTranslation();

  useEffect(() => {
    // Use window.location instead of the reactive useLocation() hook here
    // because this block must ONLY act as a boot guard on the initial page load.
    // Adding a reactive location object to a useEffect array would cause the entire
    // app to re-initialize from scratch on every route change.
    const pathSegments = window.location.pathname
      .split("/")
      .filter((segment) => segment.length > 0);
    const isTestSetupRoute = pathSegments.includes(RoutePaths.TEST_SETUP_AUTH);
    if (isDevModeOrTestStubsEnabled && isTestSetupRoute) {
      console.info(
        "Skipping all app initialization. This route allows you to set some local storage values for testing.",
      );
      return;
    }

    initApp(auth, graphQlClient, navigate, cacheHydratedPromise)
      .then((appInitializationResult: AppInitializationResult) => {
        console.info(
          "App initialization finished successfully.",
          appInitializationResult,
        );
        setAuthState(appInitializationResult.authState);
        setBackendServerAvailability(
          appInitializationResult.backendServerAvailability,
        );
        setSongsAvailability(appInitializationResult.songsAvailability);
        setSongs(appInitializationResult.songs);
      })
      .catch((e) => {
        console.error("App initialization failed.", e);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const resetProgress = () => {
    setOfflineUpdateSummary({ currentState: CurrentState.NOT_RUNNING });
    setDownloadOfFilesNecessary(false);
    checkIfDownloadOfFilesIsNecessary(songs, setDownloadOfFilesNecessary);
  };

  useEffect(() => {
    checkIfDownloadOfFilesIsNecessary(songs, setDownloadOfFilesNecessary);
  }, [songs]);

  const lookupSong: LookupSongFunction = (id: string): SongUb | undefined => {
    return songs ? songs.find((s) => s.id === id) : undefined;
  };

  const startUpdate = () => {
    if (songs) {
      update(songs, auth, setOfflineUpdateSummary);
    }
  };

  const playlistNode = (identifier: string): ReactNode => {
    return (
      <Playlist
        playlistIdentifier={identifier}
        lookupSong={lookupSong}
        auth={auth}
        graphQlClient={graphQlClient}
      />
    );
  };

  const playlistUrlImportDialogNode = (identifier: string): ReactNode => {
    return <PlaylistUrlImportDialog importIdentifier={identifier} />;
  };

  const joinCrewNode = (identifier: string): ReactNode => {
    return <JoinCrewDialog joinIdentifier={identifier} />;
  };

  const isTokenRefreshHarnessRoute = location.pathname
    .split("/")
    .filter((segment) => segment.length > 0)
    .includes(RoutePaths.TEST_SETUP_AUTH);

  return (
    <>
      <Routes>
        <Route
          path={`/${RoutePaths.CALLBACK}`}
          element={<LoadingWithHeader message={t("app.authMessage")} />}
        />
        {isDevModeOrTestStubsEnabled && (
          <Route
            path={`/${RoutePaths.TEST_SETUP_AUTH}`}
            element={
              <Suspense
                fallback={
                  <LoadingWithHeader message={t("app.loadingTestSetup")} />
                }
              >
                <TokenRefreshHarness />
              </Suspense>
            }
          />
        )}
        <Route path={`/${RoutePaths.STUB_LOGIN}`} element={<StubLogin />} />
        <Route
          path={`/${RoutePaths.DEMO_MULTI_SELECTABLE_LIST}`}
          element={<DemoMultiSelectableList />}
        />
        <Route
          path={`/${RoutePaths.LOGOUT}`}
          element={<Logout auth={auth} navigate={navigate} />}
        />
        <Route
          path="*"
          element={
            backendServerAvailability === BackendServerAvailability.Undecided ||
            authState === AuthState.Undecided ? (
              <LoadingWithHeader message={t("app.loadingMessage")} />
            ) : authState === AuthState.LocalStorageDeletionNecessary ? (
              <LocalStorageDeletionNecessaryNotice auth={auth} />
            ) : authState === AuthState.Failed ? (
              <AuthFailedNotice logout={() => auth.logout(navigate)} />
            ) : songsAvailability === SongsAvailability.ToCIsAvailable ? (
              <Provider value={graphQlClient}>
                <Routes>
                  <Route
                    path={`/${RoutePaths.PLAYLIST}/:${playlistIdentifierParamKey}/*`}
                    element={
                      <ParamValidatorPlaylistIdentifier
                        nodeExpectingPlaylistIdentifier={playlistNode}
                      />
                    }
                  />
                  <Route
                    path={`/${RoutePaths.IMPORT_PLAYLIST}/:${playlistIdentifierParamKey}`}
                    element={
                      <ParamValidatorPlaylistIdentifier
                        nodeExpectingPlaylistIdentifier={
                          playlistUrlImportDialogNode
                        }
                        isLocalStoragePersistenceValidationDisabled={true}
                      />
                    }
                  />
                  <Route
                    path={`/${RoutePaths.JOIN_CREW}/:${paramKey}`}
                    element={
                      <ParamValidatorId
                        nodeExpectingIdentifier={joinCrewNode}
                      />
                    }
                  />
                  <Route
                    path={`/${RoutePaths.PROFILE}`}
                    element={<Profile auth={auth} />}
                  />
                  <Route
                    path={`/${RoutePaths.PROFILE}/:id`}
                    element={<ViewProfile auth={auth} />}
                  />
                  <Route
                    path={`/${RoutePaths.MANAGE_CREWS}`}
                    element={<ManageCrews />}
                  />
                  <Route
                    path={`/:${bucketParamKey}/${RoutePaths.SONGBOOKS}`}
                    element={<ManageSongbooks />}
                  />
                  <Route
                    path={`/${RoutePaths.SCAN_QR_CODE}`}
                    element={<ScanQrCode />}
                  />
                  <Route path={`/${RoutePaths.HELP}`} element={<Help />} />
                  <Route
                    path={`/${RoutePaths.INFO_CONTACT}`}
                    element={<InfoContact />}
                  />
                  <Route
                    path={`/:${bucketParamKey}/${RoutePaths.ITEMS}`}
                    element={<Items />}
                  />
                  <Route
                    path={`/:${bucketParamKey}/${RoutePaths.ITEMS}/:${itemIdParamKey}`}
                    element={
                      <ParamValidatorBucketAndItemId
                        nodeExpectingBucketAndItemId={(bucket, id) => (
                          <Item auth={auth} bucket={bucket} itemId={id} />
                        )}
                      />
                    }
                  />
                  <Route
                    path="/"
                    element={
                      <Home
                        auth={auth}
                        tocAvailability={songsAvailability}
                        backendServerAvailability={backendServerAvailability}
                        startUpdate={startUpdate}
                        resetProgress={resetProgress}
                        lookupSong={lookupSong}
                        offlineUpdateSummary={offlineUpdateSummary}
                        downloadOfFilesNecessary={downloadOfFilesNecessary}
                      />
                    }
                  />
                  <Route
                    path="/index.html"
                    element={<Navigate to="/" replace />}
                  />
                  <Route path="*" element={<InvalidRouteElement />} />
                </Routes>
              </Provider>
            ) : (
              <TocAvailabilityNotice
                logout={() => auth.logout(navigate)}
                tocAvailability={songsAvailability}
              />
            )
          }
        />
      </Routes>

      {/* Do not render an extra TokenRefreshComponent if we're inside the test harness route */}
      {authState === AuthState.OkWithOnlineAuthentication &&
        !isTokenRefreshHarnessRoute && <TokenRefreshComponent auth={auth} />}
    </>
  );
}

function checkIfDownloadOfFilesIsNecessary(
  songs: readonly SongUb[] | undefined,
  setDownloadOfFilesNecessary: (
    value: ((prevState: boolean) => boolean) | boolean,
  ) => void,
) {
  if (songs !== undefined) {
    console.debug("Checking, if offline data update is necessary.");
    new DownloadNecessityChecker()
      .isFileDownloadNecessary(songs)
      .then((isUpdateNecessary) =>
        setDownloadOfFilesNecessary(isUpdateNecessary),
      );
  } else {
    console.debug("Skipping offline data check because songs is", songs);
  }
}
